import { Fragment, useId, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { CSSProperties } from 'react'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { AgentBuilderReportData, DashboardTrendGranularity, DashboardWidget } from './types'
import {
  classifyColumns,
  formatTableCellString,
  formatFieldReferencesInText,
  formatFieldDisplayName,
  formatColumnValue,
  formatCompactColumnValue,
  formatDimensionLabel,
  formatShortDateLabel,
  formatTableCellValue,
  getBarAxisMin,
  pickLabelColumn,
  resolveChartDimension,
  resolveColumnFormat,
} from './vizUtils'
import { productThemes } from '@/lib/productBranding'
import {
  CHART_AXIS_LINE_COLOR,
  CHART_AXIS_TICK,
  CHART_BAR_TRACK,
  CHART_CATEGORY_TICK,
  CHART_GRID_COLOR,
  CHART_VALUE_LABEL_STYLE,
  chartLegendProps,
  chartTooltipProps,
  getSeriesColor,
} from './chartTheme'
import { PaginationBar } from './components/PaginationBar'
import { findDrilldownColumn } from './drilldownHierarchy'
import {
  aggregateLineChartDataByDate,
  buildColumnBarMax,
  buildExpansionRowKey,
  buildLineChartRows,
  buildPivotMatrix,
  formatLineSeriesName,
  getLineSeriesColor,
  inCellBarPercent,
  isEmptyCellValue,
  KPI_ACCENT_COLORS,
  lineSeriesColumn,
  resolveBackendPagination,
  resolveExpandableCellValue,
  resolveNumericColumns,
  shiftChartPointLabelsByYears,
  TABLE_NEGATIVE_BAR_COLOR,
  tableBarColor,
} from './vizModel'
import type { ExpandedTableResult } from './vizModel'

const TABLE_PAGE_SIZE = 100
const BAR_CHART_PAGE_SIZE = 20
const LINE_CHART_PAGE_SIZE = 100
const PRINT_CHART_WIDTH = 660
const TABLE_NEGATIVE_TEXT_CLASS = 'text-[#d64545]'
const TABLE_WRAPPER_CLASS = 'rounded-lg border border-[#e6eaef]'
const TABLE_HEAD_CELL_CLASS = 'sticky top-0 z-10 whitespace-nowrap border-b border-[#e6eaef] bg-[#f3fbfa] px-3.5 py-2.5 text-xs font-semibold text-[#475467]'
const TABLE_BODY_ROW_CLASS = 'border-b border-[#f0f2f5] transition-colors'
const TABLE_BODY_CELL_CLASS = 'h-[42px] whitespace-nowrap px-3.5 py-0 text-[13px]'

function getPrintTableStyle(columnCount: number) {
  const scale = columnCount <= 6
    ? 1
    : columnCount <= 8
      ? 0.92
      : columnCount <= 10
        ? 0.84
        : columnCount <= 12
          ? 0.76
          : 0.68

  return {
    '--dashboard-print-table-scale': scale,
    '--dashboard-print-column-count': columnCount,
  } as CSSProperties
}

function getNextSortDirection(isActive: boolean, currentDirection: 'asc' | 'desc' | null | undefined) {
  if (!isActive) return 'desc'
  return currentDirection === 'asc' ? 'desc' : 'asc'
}

export type { ExpandedTableResult } from './vizModel'

type TableColumnAlign = 'left' | 'right'

function renderEmptyNumericCell(): ReactNode {
  return <span className="text-[#b0b8c3]">–</span>
}

/** Plain (non-bar) cell text. Empty values in a numeric column show a grey en dash; everything else keeps formatTableCellValue. */
function renderPlainCell(value: unknown, column: string, columnFormats: AgentBuilderReportData['columnFormats'], numeric: boolean): ReactNode {
  if (numeric && isEmptyCellValue(value)) return renderEmptyNumericCell()
  return formatTableCellValue(value, column, columnFormats)
}

function tableHeadCellClass(align: TableColumnAlign): string {
  return `${TABLE_HEAD_CELL_CLASS} ${align === 'right' ? 'text-right' : 'text-left'}`
}

function tableBodyCellClass(numeric: boolean, value?: unknown): string {
  if (!numeric) return `${TABLE_BODY_CELL_CLASS} text-left text-gray-700`
  const negative = typeof value === 'number' && value < 0
  return `${TABLE_BODY_CELL_CLASS} text-right tabular-nums ${negative ? TABLE_NEGATIVE_TEXT_CLASS : 'text-[#1d2433]'}`
}

/**
 * In-cell bar, following `.cb` in mockups/dashboard.css. The value stays in normal flow (not absolute),
 * so the cell is at least as wide as its text and the value is never clipped or covered by the fill.
 */
function InCellBar({ value, max, color, label }: { value: number; max: number; color: string; label: string }) {
  const negative = value < 0
  const width = inCellBarPercent(value, max)
  return (
    <div className="relative block h-[26px] overflow-hidden rounded-md bg-[#f3f5f8]">
      {value !== 0 ? (
        <div
          className="absolute inset-y-0 left-0 rounded-md opacity-[0.35]"
          style={{ width: `${width}%`, background: negative ? TABLE_NEGATIVE_BAR_COLOR : color }}
        />
      ) : null}
      <span className={`relative block px-2.5 text-right text-[12.5px] font-medium leading-[26px] tabular-nums ${negative ? TABLE_NEGATIVE_TEXT_CLASS : 'text-[#1d2433]'}`}>
        {label}
      </span>
    </div>
  )
}

type ExpandableTableProps = {
  data: AgentBuilderReportData
  page: number
  onPageChange?: (p: number) => void
  showPagination: boolean
  onSelect?: (field: string, value: string) => void
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void
  sortColumn?: string | null
  sortDirection?: 'asc' | 'desc' | null
  /** Draw in-cell bars for numeric values (data-bar tables). */
  bars?: boolean
  expandedRows?: Record<string, ExpandedTableResult>
  loadingRowKeys?: Record<string, boolean>
  onExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => void
  canExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => boolean
  buildRowKey?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => string
}

function ExpandableTable({
  data,
  page,
  onPageChange,
  showPagination,
  onSelect,
  onSortChange,
  sortColumn,
  sortDirection,
  bars = false,
  expandedRows = {},
  loadingRowKeys = {},
  onExpandRow,
  canExpandRow,
  buildRowKey = buildExpansionRowKey,
}: ExpandableTableProps) {
  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.rows.length / TABLE_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleRows = onPageChange && !backendPagination
    ? data.rows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE)
    : data.rows
  const numericColumns = resolveNumericColumns(data)
  const barColumns = data.columns.filter((column) => numericColumns.has(column))
  // Scale is per column, from the top-level rows on this page. Child rows reuse it and clamp at 100%.
  const columnBarMax = bars
    ? buildColumnBarMax(data.columns, visibleRows, (row, column, columnIndex) => resolveExpandableCellValue(row, column, columnIndex, data.columns))
    : {}

  function renderRows(rows: Record<string, unknown>[], columns: string[], parentKey = '', depth = 0): React.ReactNode[] {
    return rows.flatMap((row) => {
      const rowKey = buildRowKey(row, columns, parentKey)
      const expanded = expandedRows[rowKey]
      const isLoading = loadingRowKeys[rowKey] === true
      const canExpand = Boolean(onExpandRow && (canExpandRow ? canExpandRow(row, columns, parentKey || undefined) : true))
      const isGroupRow = Boolean(onExpandRow)
      const stringColumn = columns.find((column) => typeof row[column] === 'string')
      const rowContent = (
        <Fragment key={rowKey}>
          <tr
            aria-expanded={canExpand ? Boolean(expanded) : undefined}
            className={`${TABLE_BODY_ROW_CLASS} ${depth === 0 && isGroupRow ? 'bg-cyan-50/30 font-medium' : ''} ${
              onExpandRow || onSelect ? 'feature-hover-surface cursor-pointer' : 'hover:bg-[#f8fbfb]'
            }`}
            onClick={() => {
              // Let the panel resolve the hierarchy from the actual row. The
              // local affordance check is only visual; relying on it here
              // made valid rows inert when API column metadata differed from
              // the row keys.
              if (onExpandRow) onExpandRow(row, columns, parentKey || undefined)
              else if (onSelect && stringColumn) onSelect(stringColumn, String(row[stringColumn]))
            }}
          >
            <td className="h-[42px] w-10 px-2 py-0 text-center" style={{ paddingLeft: `${8 + depth * 18}px` }}>
              {canExpand ? (
                <button
                  type="button"
                  aria-label={expanded ? 'Collapse group' : 'Expand group'}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-cyan-100 hover:text-cyan-800 disabled:cursor-default disabled:opacity-50"
                  disabled={isLoading}
                  onClick={(event) => {
                    event.stopPropagation()
                    onExpandRow?.(row, columns, parentKey || undefined)
                  }}
                >
                  {isLoading ? (
                    <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
                      <path d="M20 12a8 8 0 0 1-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 20 20" fill="none">
                      <path d={expanded ? 'M5 7.5 10 12.5 15 7.5' : 'M7.5 5 12.5 10 7.5 15'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ) : null}
            </td>
            {data.columns.map((column, columnIndex) => {
              const value = resolveExpandableCellValue(row, column, columnIndex, columns)
              const numeric = numericColumns.has(column)
              if (bars && typeof value === 'number') {
                const barColorIndex = Math.max(0, barColumns.indexOf(column))
                return (
                  <td key={column} className={`${TABLE_BODY_CELL_CLASS} min-w-[10rem]`}>
                    <InCellBar
                      value={value}
                      max={columnBarMax[column] ?? 1}
                      color={tableBarColor(barColorIndex)}
                      label={formatColumnValue(value, column, data.columnFormats)}
                    />
                  </td>
                )
              }
              return (
                <td key={column} className={tableBodyCellClass(numeric, value)}>
                  {renderPlainCell(value, column, data.columnFormats, numeric)}
                </td>
              )
            })}
          </tr>
          {expanded ? (
            <>
              {expanded.rows.length > 0 ? (
                renderRows(expanded.rows, expanded.columns, rowKey, depth + 1).map((child) => child)
              ) : (
                <tr key={`${rowKey}/empty`} className="border-b border-[#f0f2f5]">
                  <td colSpan={Math.max(data.columns.length + 1, 1)} className="px-3 py-2 text-sm text-gray-500" style={{ paddingLeft: `${26 + depth * 18}px` }}>
                    No child rows found.
                  </td>
                </tr>
              )}
            </>
          ) : null}
        </Fragment>
      )
      return [rowContent]
    })
  }

  return (
    <>
      <div className={`dashboard-print-table-scroll overflow-auto ${TABLE_WRAPPER_CLASS} ${showPagination ? 'max-h-[32rem]' : ''}`} style={getPrintTableStyle(data.columns.length + 1)}>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky top-0 z-10 w-9 border-b border-[#e6eaef] bg-[#f3fbfa] px-2 py-2.5" />
              {data.columns.map((column) => (
                <SortableHeader
                  key={column}
                  column={column}
                  label={data.columnLabels?.[column]}
                  active={sortColumn === column}
                  direction={sortDirection}
                  onSortChange={onSortChange}
                  align={numericColumns.has(column) ? 'right' : 'left'}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0
              ? <tr><td colSpan={Math.max(data.columns.length + 1, 1)} className="px-3 py-6 text-center text-sm text-gray-400">No rows to display.</td></tr>
              : renderRows(visibleRows, data.columns)}
          </tbody>
        </table>
      </div>
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.rows.length > TABLE_PAGE_SIZE) && (
        <PaginationBar page={currentPage} totalRows={backendPagination ? backendPagination.totalRows : data.rows.length} pageSize={backendPagination ? backendPagination.pageSize : TABLE_PAGE_SIZE} onPageChange={onPageChange} />
      )}
    </>
  )
}

function SortableHeader({
  column,
  label,
  active,
  direction,
  onSortChange,
  align = 'left',
}: {
  column: string
  /** Configured header text; shown as written instead of the cleaned-up column name. */
  label?: string
  active: boolean
  direction?: 'asc' | 'desc' | null
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void
  /** Numeric columns right-align their header; the sort indicator still trails the label. */
  align?: TableColumnAlign
}) {
  const indicator = active ? (direction === 'asc' ? '↑' : '↓') : '↕'
  const text = label ?? formatFieldReferencesInText(column)

  if (!onSortChange) {
    return <th className={tableHeadCellClass(align)}>{text}</th>
  }

  return (
    <th className={tableHeadCellClass(align)}>
      <button
        type="button"
        className={`inline-flex items-center gap-1 rounded px-0 py-0.5 transition-colors hover:text-[#0f8f82] ${align === 'right' ? 'w-full justify-end' : 'text-left'}`}
        onClick={() => onSortChange(column, getNextSortDirection(active, direction))}
      >
        <span>{text}</span>
        <span className={`text-[11px] ${active ? 'text-[#0f8f82]' : 'text-[#b6c2cf]'}`}>{indicator}</span>
      </button>
    </th>
  )
}

function getKpiGridColumns(count: number) {
  if (count <= 1) return 'grid-cols-1'
  if (count === 2) return 'grid-cols-1 sm:grid-cols-2'
  if (count === 3) return 'grid-cols-1 sm:grid-cols-3'
  return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
}

export function KpiViz({ data, onSelect }: { data: AgentBuilderReportData; onSelect?: (field: string, value: string) => void }) {
  const { dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const labelCol = useMemo(() => pickLabelColumn(dimensionCols, data.rows), [dimensionCols, data.rows])

  if (data.kpiCards.length === 0) return <BarViz data={data} onSelect={onSelect} />
  return (
    <div className={`grid gap-4 py-2 ${getKpiGridColumns(data.kpiCards.length)}`}>
      {data.kpiCards.map((kpi, i) => (
        <div
          key={i}
          className={`relative flex min-h-[96px] flex-col justify-between overflow-hidden rounded-2xl border px-5 py-3 text-white shadow-sm ${onSelect ? 'cursor-pointer brightness-100 transition hover:brightness-95' : ''}`}
          style={{
            borderColor: KPI_ACCENT_COLORS[i % KPI_ACCENT_COLORS.length],
            background: KPI_ACCENT_COLORS[i % KPI_ACCENT_COLORS.length],
          }}
          onClick={() => onSelect?.(labelCol ?? '', kpi.label)}
        >
          <p className="text-3xl font-bold tabular-nums text-white">
            {formatColumnValue(kpi.value, kpi.measureKey || kpi.measureLabel, data.columnFormats)}
            {kpi.unit && <span className="ml-1 text-sm font-normal text-white/75">{kpi.unit}</span>}
          </p>
          {kpi.displayLabel || kpi.measureLabel ? <p className="mt-2 text-xs font-medium text-white/75">{kpi.displayLabel ?? formatFieldReferencesInText(kpi.measureLabel ?? '')}</p> : null}
        </div>
      ))}
    </div>
  )
}

export function DataBarTableViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  onSelect,
  onSortChange,
  sortColumn,
  sortDirection,
  expandedRows,
  loadingRowKeys,
  onExpandRow,
  canExpandRow,
  buildRowKey,
  pivot = false,
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  onSelect?: (field: string, value: string) => void
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void
  sortColumn?: string | null
  sortDirection?: 'asc' | 'desc' | null
  expandedRows?: Record<string, ExpandedTableResult>
  loadingRowKeys?: Record<string, boolean>
  onExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => void
  canExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => boolean
  buildRowKey?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => string
  pivot?: boolean
}) {
  const backendPaginationPivot = pivot ? resolveBackendPagination(data) : null
  // In pivot mode aggregation must span the full dataset — otherwise per-cell sums and pivot
  // columns depend on which raw rows happened to land on the current backend page. When the
  // backend is paginating raw rows, `data.rows` is only a slice, so surface a warning and skip
  // the pagination bar (we still aggregate what we have; totals in that state are page-scoped).
  const pivotPartialData = !!backendPaginationPivot && data.rows.length < backendPaginationPivot.totalRows
  const pivotClassified = pivot ? classifyColumns(data.columns, data.rows) : null
  const pivotRowLabel = pivotClassified?.dimensionCols[0] ?? null
  const pivotColumnLabel = pivotClassified?.dimensionCols[1] ?? null
  const pivotValueColumn = pivotClassified?.measureCols[0] ?? null

  if (pivot && pivotRowLabel && pivotColumnLabel && pivotValueColumn) {
    // Empty measure values stay out of the matrix so their cell shows an empty dash.
    // Totals are unchanged because they would have added 0.
    const { rowKeys: allRowKeys, columnKeys, matrix } = buildPivotMatrix(data.rows, pivotRowLabel, pivotColumnLabel, pivotValueColumn)
    const pivotPageCount = Math.max(1, Math.ceil(allRowKeys.length / TABLE_PAGE_SIZE))
    const pivotCurrentPage = Math.min(Math.max(page, 1), pivotPageCount)
    const rowKeys = onPageChange && !pivotPartialData
      ? allRowKeys.slice((pivotCurrentPage - 1) * TABLE_PAGE_SIZE, pivotCurrentPage * TABLE_PAGE_SIZE)
      : allRowKeys
    // Bars scale per pivot column, over the rows on this page.
    const pivotColumnMax = new Map<string, number>()
    columnKeys.forEach((columnKey) => {
      const max = rowKeys.reduce((acc, rowKey) => Math.max(acc, Math.abs(matrix.get(rowKey)?.get(columnKey) ?? 0)), 0)
      pivotColumnMax.set(columnKey, max || 1)
    })

    return (
      <>
        {pivotPartialData && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Pivot totals reflect only the {data.rows.length.toLocaleString()} row(s) on the current backend page (of {backendPaginationPivot!.totalRows.toLocaleString()}). Widen the query or increase page size to see complete aggregates.
          </div>
        )}
        <div
          className={`dashboard-print-table-scroll overflow-auto ${TABLE_WRAPPER_CLASS} ${showPagination ? 'max-h-[32rem]' : ''}`}
          style={getPrintTableStyle(columnKeys.length + 2)}
        >
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={tableHeadCellClass('left')}>{formatFieldReferencesInText(pivotRowLabel)}</th>
                {columnKeys.map((columnKey) => (
                  <th key={columnKey} className={tableHeadCellClass('right')}>
                    {formatFieldReferencesInText(pivotColumnLabel)}: {formatFieldReferencesInText(columnKey)}
                  </th>
                ))}
                <th className={tableHeadCellClass('right')}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rowKeys.map((rowKey) => {
                const rowMap = matrix.get(rowKey) ?? new Map<string, number>()
                const total = columnKeys.reduce((sum, columnKey) => sum + (rowMap.get(columnKey) ?? 0), 0)
                return (
                  <tr key={rowKey} className={`${TABLE_BODY_ROW_CLASS} hover:bg-[#f8fbfb]`}>
                    <td className={`${TABLE_BODY_CELL_CLASS} text-left font-medium text-gray-700`}>{formatFieldReferencesInText(rowKey)}</td>
                    {columnKeys.map((columnKey, columnIndex) => {
                      const cell = rowMap.get(columnKey)
                      if (cell === undefined) {
                        return (
                          <td key={columnKey} className={`${TABLE_BODY_CELL_CLASS} text-right`}>
                            {renderEmptyNumericCell()}
                          </td>
                        )
                      }
                      return (
                        <td key={columnKey} className={`${TABLE_BODY_CELL_CLASS} min-w-[10rem]`}>
                          <InCellBar
                            value={cell}
                            max={pivotColumnMax.get(columnKey) ?? 1}
                            color={tableBarColor(columnIndex)}
                            label={formatColumnValue(cell, pivotValueColumn, data.columnFormats)}
                          />
                        </td>
                      )
                    })}
                    <td className={`${tableBodyCellClass(true, total)} font-semibold`}>
                      {formatColumnValue(total, pivotValueColumn, data.columnFormats)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {showPagination && onPageChange && !pivotPartialData && allRowKeys.length > TABLE_PAGE_SIZE && (
          <PaginationBar
            page={pivotCurrentPage}
            totalRows={allRowKeys.length}
            pageSize={TABLE_PAGE_SIZE}
            onPageChange={onPageChange}
          />
        )}
      </>
    )
  }

  if (onExpandRow) {
    return (
      <ExpandableTable
        data={data}
        page={page}
        onPageChange={onPageChange}
        showPagination={showPagination}
        onSortChange={onSortChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        expandedRows={expandedRows}
        loadingRowKeys={loadingRowKeys}
        onExpandRow={onExpandRow}
        canExpandRow={canExpandRow}
        buildRowKey={buildRowKey}
        bars
      />
    )
  }
  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.rows.length / TABLE_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleRows = onPageChange && !backendPagination
    ? data.rows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE)
    : data.rows
  const numericColumns = resolveNumericColumns(data)
  const barColumns = data.columns.filter((column) => numericColumns.has(column))
  const { dimensionCols } = classifyColumns(data.columns, visibleRows)
  const labelCol = dimensionCols[0] ?? data.columns[0]
  const columnBarMax = buildColumnBarMax(data.columns, visibleRows, (row, column) => row[column])

  return (
    <>
      <div
        className={`dashboard-print-table-scroll overflow-auto ${TABLE_WRAPPER_CLASS} ${showPagination ? 'max-h-[32rem]' : ''}`}
        style={getPrintTableStyle(data.columns.length)}
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {data.columns.map((column) => (
                <SortableHeader
                  key={column}
                  column={column}
                  label={data.columnLabels?.[column]}
                  active={sortColumn === column}
                  direction={sortDirection}
                  onSortChange={onSortChange}
                  align={numericColumns.has(column) ? 'right' : 'left'}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(data.columns.length, 1)} className="px-3 py-6 text-center text-sm text-gray-400">
                  No rows to display.
                </td>
              </tr>
            ) : (
              visibleRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${TABLE_BODY_ROW_CLASS} ${onSelect ? 'feature-hover-surface cursor-pointer' : 'hover:bg-[#f8fbfb]'}`}
                  onClick={() => {
                    if (!onSelect || !labelCol) return
                    onSelect(labelCol, String(row[labelCol] ?? ''))
                  }}
                >
                  {data.columns.map((column) => {
                    const value = row[column]
                    const numeric = numericColumns.has(column)
                    if (typeof value === 'number') {
                      const barColorIndex = Math.max(0, barColumns.indexOf(column))
                      return (
                        <td key={column} className={`${TABLE_BODY_CELL_CLASS} min-w-[12rem]`}>
                          <InCellBar
                            value={value}
                            max={columnBarMax[column] ?? 1}
                            color={tableBarColor(barColorIndex)}
                            label={formatColumnValue(value, column, data.columnFormats)}
                          />
                        </td>
                      )
                    }

                    return (
                      <td key={column} className={tableBodyCellClass(numeric, value)}>
                        {renderPlainCell(value, column, data.columnFormats, numeric)}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.rows.length > TABLE_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : data.rows.length}
          pageSize={backendPagination ? backendPagination.pageSize : TABLE_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

export function PivotTableViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  expandedRows = {},
  loadingRowKeys = {},
  onExpandRow,
  canExpandRow,
  buildRowKey = buildExpansionRowKey,
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  expandedRows?: Record<string, ExpandedTableResult>
  loadingRowKeys?: Record<string, boolean>
  onExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => void
  canExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => boolean
  buildRowKey?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => string
}) {
  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.rows.length / TABLE_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleRows = onPageChange && !backendPagination
    ? data.rows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE)
    : data.rows
  const { measureCols, dimensionCols } = classifyColumns(data.columns, visibleRows)
  const rowLabel = dimensionCols[0] ?? data.columns[0]
  const columnLabel = dimensionCols[1]
  const valueColumn = measureCols[0]

  if (!rowLabel || !columnLabel || !valueColumn) {
    return <TableViz data={data} page={page} onPageChange={onPageChange} showPagination={showPagination} />
  }

  const measureColumn: string = valueColumn
  // Empty measure values stay out of the matrix so their cell shows an empty dash.
  const { rowKeys, columnKeys, matrix } = buildPivotMatrix(visibleRows, rowLabel, columnLabel, valueColumn)

  function renderPivotChildRows(expanded: ExpandedTableResult, parentKey: string, depth: number): React.ReactNode {
    const span = columnKeys.length + 2
    if (expanded.rows.length === 0) {
      return (
        <tr key={`${parentKey}/empty`} className="border-b border-[#f0f2f5]">
          <td colSpan={span} className="px-3 py-2 text-sm text-gray-500" style={{ paddingLeft: `${12 + depth * 18}px` }}>
            No child rows found.
          </td>
        </tr>
      )
    }
    const dimensionColumn = findDrilldownColumn(expanded.columns, expanded.dimension) ?? expanded.dimension
    const childValueColumn = findDrilldownColumn(expanded.columns, measureColumn)
      ?? expanded.columns.find((column) => expanded.rows.some((row) => typeof row[column] === 'number'))
    return expanded.rows.map((row) => {
      const childKey = buildRowKey(row, expanded.columns, parentKey)
      const childExpanded = expandedRows[childKey]
      const childLoading = loadingRowKeys[childKey] === true
      const childCanExpand = Boolean(onExpandRow && (canExpandRow ? canExpandRow(row, expanded.columns, parentKey) : true))
      const label = row[dimensionColumn]
      const measure = childValueColumn ? row[childValueColumn] : undefined
      return (
        <Fragment key={childKey}>
          <tr
            aria-expanded={childCanExpand ? Boolean(childExpanded) : undefined}
            className={`${TABLE_BODY_ROW_CLASS} hover:bg-[#f8fbfb] ${childCanExpand ? 'cursor-pointer' : ''}`}
            onClick={childCanExpand ? () => onExpandRow?.(row, expanded.columns, parentKey) : undefined}
          >
            <td className={`${TABLE_BODY_CELL_CLASS} text-left text-gray-700`} style={{ paddingLeft: `${12 + depth * 18}px` }}>
              <span className="inline-flex items-center gap-1">
                {childCanExpand ? (
                  <button
                    type="button"
                    aria-label={childExpanded ? 'Collapse group' : 'Expand group'}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-cyan-100 hover:text-cyan-800 disabled:cursor-default disabled:opacity-50"
                    disabled={childLoading}
                    onClick={(event) => {
                      event.stopPropagation()
                      onExpandRow?.(row, expanded.columns, parentKey)
                    }}
                  >
                    <svg aria-hidden="true" className={`h-4 w-4 ${childLoading ? 'animate-pulse' : ''}`} viewBox="0 0 20 20" fill="none">
                      <path d={childExpanded ? 'M5 7.5 10 12.5 15 7.5' : 'M7.5 5 12.5 10 7.5 15'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ) : <span className="inline-block h-6 w-6" />}
                {label === null || label === undefined ? '-' : formatFieldReferencesInText(formatTableCellString(String(label)))}
              </span>
            </td>
            {columnKeys.map((columnKey) => <td key={columnKey} className={TABLE_BODY_CELL_CLASS} />)}
            <td className={tableBodyCellClass(true, measure)}>
              {typeof measure === 'number' ? formatColumnValue(measure, childValueColumn ?? measureColumn, data.columnFormats) : renderEmptyNumericCell()}
            </td>
          </tr>
          {childExpanded ? renderPivotChildRows(childExpanded, childKey, depth + 1) : null}
        </Fragment>
      )
    })
  }

  return (
    <>
      <div
        className={`dashboard-print-table-scroll overflow-auto ${TABLE_WRAPPER_CLASS} ${showPagination ? 'max-h-[32rem]' : ''}`}
        style={getPrintTableStyle(columnKeys.length + 2)}
      >
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={tableHeadCellClass('left')}>
                {formatFieldReferencesInText(rowLabel)}
              </th>
              {columnKeys.map((columnKey) => (
                <th key={columnKey} className={tableHeadCellClass('right')}>
                  {formatFieldReferencesInText(columnLabel)}: {formatFieldReferencesInText(columnKey)}
                </th>
              ))}
              <th className={tableHeadCellClass('right')}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rowKeys.map((rowKey) => {
              const rowMap = matrix.get(rowKey) ?? new Map<string, number>()
              const total = columnKeys.reduce((sum, columnKey) => sum + (rowMap.get(columnKey) ?? 0), 0)
              // Drill is keyed off the row-label dimension only; the pivot column
              // field never participates in the drill order.
              const drillRow: Record<string, unknown> = { [rowLabel]: rowKey }
              const drillColumns = [rowLabel]
              const drillKey = buildRowKey(drillRow, drillColumns, '')
              const expanded = expandedRows[drillKey]
              const isLoading = loadingRowKeys[drillKey] === true
              const canExpand = Boolean(onExpandRow && rowKey !== '—' && (canExpandRow ? canExpandRow(drillRow, drillColumns, undefined) : true))
              return (
                <Fragment key={rowKey}>
                  <tr
                    aria-expanded={canExpand ? Boolean(expanded) : undefined}
                    className={`${TABLE_BODY_ROW_CLASS} hover:bg-[#f8fbfb] ${canExpand ? 'cursor-pointer' : ''}`}
                    onClick={canExpand ? () => onExpandRow?.(drillRow, drillColumns, undefined) : undefined}
                  >
                    <td className={`${TABLE_BODY_CELL_CLASS} text-left font-medium text-gray-700`}>
                      <span className="inline-flex items-center gap-1">
                        {onExpandRow ? (
                          canExpand ? (
                            <button
                              type="button"
                              aria-label={expanded ? 'Collapse group' : 'Expand group'}
                              className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover:bg-cyan-100 hover:text-cyan-800 disabled:cursor-default disabled:opacity-50"
                              disabled={isLoading}
                              onClick={(event) => {
                                event.stopPropagation()
                                onExpandRow(drillRow, drillColumns, undefined)
                              }}
                            >
                              <svg aria-hidden="true" className={`h-4 w-4 ${isLoading ? 'animate-pulse' : ''}`} viewBox="0 0 20 20" fill="none">
                                <path d={expanded ? 'M5 7.5 10 12.5 15 7.5' : 'M7.5 5 12.5 10 7.5 15'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          ) : <span className="inline-block h-6 w-6" />
                        ) : null}
                        {formatFieldReferencesInText(rowKey)}
                      </span>
                    </td>
                    {columnKeys.map((columnKey) => {
                      const cell = rowMap.get(columnKey)
                      return (
                        <td key={columnKey} className={tableBodyCellClass(true, cell)}>
                          {cell === undefined ? renderEmptyNumericCell() : formatColumnValue(cell, valueColumn, data.columnFormats)}
                        </td>
                      )
                    })}
                    <td className={`${tableBodyCellClass(true, total)} font-semibold`}>
                      {formatColumnValue(total, valueColumn, data.columnFormats)}
                    </td>
                  </tr>
                  {expanded ? renderPivotChildRows(expanded, drillKey, 1) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.rows.length > TABLE_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : data.rows.length}
          pageSize={backendPagination ? backendPagination.pageSize : TABLE_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

/* eslint-disable react-refresh/only-export-components -- pure BarViz prop builders, exported for Viz.bar.test.tsx */
// Mock-up bar chart styling (mockups/charts.js partnerBars). Pure prop builders, so the tests can check them without a DOM.
export const BAR_CHART_MARGIN = { top: 0, right: 64, left: 0, bottom: 0 } as const
export const COLUMN_CHART_MARGIN = { top: 20, right: 8, left: 0, bottom: 0 } as const
const BAR_RADIUS: [number, number, number, number] = [0, 6, 6, 0]
const COLUMN_RADIUS: [number, number, number, number] = [6, 6, 0, 0]
const COLUMN_LABEL_MAX_LENGTH = 24
const COLUMN_LABEL_ANGLE = 35
const GROUPED_COLUMN_GAP = 4
const GROUPED_COLUMN_LABEL_STYLE = { ...CHART_VALUE_LABEL_STYLE, fontSize: 10.5 } as const

/** Legend, tooltip and label name for a series: a configured column label wins over the derived name. */
export function buildBarSeriesName(name: string, columnLabels: Record<string, string> | null | undefined): string {
  return columnLabels?.[name] ?? formatFieldDisplayName(name)
}

/** Value axis (x for bars, y for columns): round ticks from the bar-axis minimum, no decimals, compact tick labels. */
export function buildBarValueAxisProps(values: number[], formatTick: (value: number) => string, printMode: boolean) {
  return {
    type: 'number' as const,
    tick: printMode ? { ...CHART_AXIS_TICK, fontSize: 9 } : CHART_AXIS_TICK,
    axisLine: false,
    tickLine: false,
    tickCount: 5,
    allowDecimals: false,
    domain: [getBarAxisMin(values), 'auto'] as [number, 'auto'],
    tickFormatter: (value: unknown) => formatTick(Number(value)),
  }
}

/** Category (y) axis: wider labels on screen, smaller in print. */
export function buildBarCategoryAxisProps(printMode: boolean) {
  return {
    width: printMode ? 92 : 120,
    axisLine: false,
    tickLine: false,
    tick: printMode ? { ...CHART_CATEGORY_TICK, fontSize: 9 } : CHART_CATEGORY_TICK,
  }
}

function columnLabelLayout(labels: string[], printMode: boolean) {
  const fontSize = printMode ? 9 : 11.5
  const longest = Math.max(0, ...labels.map((label) => Math.min(label.length, COLUMN_LABEL_MAX_LENGTH)))
  return { fontSize, longest, angled: longest > 8 || labels.length > 12 }
}

const columnValueAxisWidth = (printMode: boolean) => (printMode ? 44 : 56)

/** Category (x) axis of a column chart. Long or many labels are angled so they don't overlap. */
export function buildColumnCategoryAxisProps(labels: string[], printMode: boolean) {
  const { fontSize, longest, angled } = columnLabelLayout(labels, printMode)
  return {
    axisLine: false,
    tickLine: false,
    interval: 0 as const,
    tick: angled
      ? { ...CHART_CATEGORY_TICK, fontSize, angle: -COLUMN_LABEL_ANGLE, textAnchor: 'end' as const }
      : { ...CHART_CATEGORY_TICK, fontSize },
    height: angled ? Math.round(16 + longest * fontSize * 0.33) : 28,
  }
}

/** An angled label ends under its column, so the first one needs room on the left beyond the value axis. */
export function buildColumnChartMargin(labels: string[], printMode: boolean) {
  const { fontSize, longest, angled } = columnLabelLayout(labels, printMode)
  const reach = angled ? longest * fontSize * 0.55 * Math.cos((COLUMN_LABEL_ANGLE * Math.PI) / 180) : 0
  return { ...COLUMN_CHART_MARGIN, left: Math.max(0, Math.round(reach - columnValueAxisWidth(printMode))) }
}

export const shortenColumnLabel = (label: string) =>
  label.length > COLUMN_LABEL_MAX_LENGTH ? `${label.slice(0, COLUMN_LABEL_MAX_LENGTH - 1)}…` : label

/** One bar series. Single-series charts get the background track; clustered charts do not. */
export function buildBarSeriesProps({
  dataKey,
  name,
  color,
  single,
  printMode,
  columns = false,
}: {
  dataKey: string
  name: string
  color: string
  single: boolean
  printMode: boolean
  columns?: boolean
}) {
  return {
    dataKey,
    name,
    fill: color,
    radius: columns ? COLUMN_RADIUS : BAR_RADIUS,
    maxBarSize: single ? 26 : columns ? 28 : 16,
    isAnimationActive: !printMode,
    ...(single ? { background: CHART_BAR_TRACK } : {}),
  }
}

/** Value label at the end of each bar, e.g. $1.17M: to the right of a bar, above a column. */
export function buildBarValueLabelProps(formatValue: (value: number) => string, columns = false) {
  return {
    position: columns ? ('top' as const) : ('right' as const),
    formatter: (value: unknown) => formatValue(Number(value)),
    style: CHART_VALUE_LABEL_STYLE,
  }
}

/** A clustered column's value label, left out when it would run into the next column's; the tooltip still has it. */
export function buildGroupedColumnLabelProps(formatValue: (value: number) => string) {
  return {
    position: 'top' as const,
    style: GROUPED_COLUMN_LABEL_STYLE,
    valueAccessor: (entry: { value?: unknown; width?: number }) => {
      const value = Number(Array.isArray(entry.value) ? entry.value[1] : entry.value)
      if (!Number.isFinite(value)) return null
      const text = formatValue(value)
      return text.length * GROUPED_COLUMN_LABEL_STYLE.fontSize * 0.6 <= (entry.width ?? 0) + GROUPED_COLUMN_GAP ? text : null
    },
  }
}

/* eslint-enable react-refresh/only-export-components */

/** The grid, axes and margin for bars across or for columns, shared by BarViz and GroupedBarViz. */
function buildBarLayout({
  columns,
  categories,
  valueAxis,
  formatCategory,
  printMode,
}: {
  columns: boolean
  categories: string[]
  valueAxis: ReturnType<typeof buildBarValueAxisProps>
  formatCategory: (value: unknown) => string
  printMode: boolean
}) {
  if (!columns) {
    return {
      layout: 'vertical' as const,
      margin: BAR_CHART_MARGIN,
      categoryAxisHeight: null,
      grid: <CartesianGrid stroke={CHART_GRID_COLOR} horizontal={false} />,
      axes: (
        <>
          <XAxis {...valueAxis} />
          <YAxis type="category" dataKey="label" {...buildBarCategoryAxisProps(printMode)} tickFormatter={formatCategory} />
        </>
      ),
    }
  }
  const categoryAxis = buildColumnCategoryAxisProps(categories, printMode)
  return {
    layout: 'horizontal' as const,
    margin: buildColumnChartMargin(categories, printMode),
    categoryAxisHeight: categoryAxis.height,
    grid: <CartesianGrid stroke={CHART_GRID_COLOR} vertical={false} />,
    axes: (
      <>
        <XAxis type="category" dataKey="label" {...categoryAxis} tickFormatter={(value) => shortenColumnLabel(formatCategory(value))} />
        <YAxis {...valueAxis} width={columnValueAxisWidth(printMode)} />
      </>
    ),
  }
}

export function BarViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  printMode = false,
  orientation,
  onSelect,
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  printMode?: boolean
  orientation?: DashboardWidget['orientation']
  onSelect?: (field: string, value: string) => void
}) {
  const { measureCols, dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const activeMeasure = measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
  const dimension = resolveChartDimension(data.dimensions, dimensionCols, data.rows)
  const formatCategory = (value: unknown) => formatDimensionLabel(String(value ?? ''), dimension, data.columnFormats)
  if (data.chartData.length === 0) return <p className="py-4 text-sm text-gray-400">No chart data available.</p>

  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.chartData.length / BAR_CHART_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleChartData = onPageChange && !backendPagination
    ? data.chartData.slice((currentPage - 1) * BAR_CHART_PAGE_SIZE, currentPage * BAR_CHART_PAGE_SIZE)
    : data.chartData
  const columns = orientation === 'vertical'
  const values = visibleChartData.map((point) => point.value)
  const formatValue = (value: number) => formatCompactColumnValue(value, activeMeasure, data.columnFormats)
  const layout = buildBarLayout({
    columns,
    categories: visibleChartData.map((point) => formatCategory(point.label)),
    valueAxis: buildBarValueAxisProps(values, formatValue, printMode),
    formatCategory,
    printMode,
  })
  const chartHeight = layout.categoryAxisHeight !== null
    ? (printMode ? 200 : 260) + layout.categoryAxisHeight
    : printMode
      ? Math.max(180, Math.min(520, visibleChartData.length * 28 + 48))
      : Math.max(180, visibleChartData.length * 38 + 48)
  const chart = (
    <BarChart
      data={visibleChartData}
      layout={layout.layout}
      margin={layout.margin}
      barCategoryGap="28%"
      width={printMode ? PRINT_CHART_WIDTH : undefined}
      height={printMode ? chartHeight : undefined}
    >
      {layout.grid}
      {layout.axes}
      {!printMode ? (
        <RechartsTooltip
          {...chartTooltipProps}
          labelFormatter={formatCategory}
          formatter={(value) => formatColumnValue(Number(value), activeMeasure, data.columnFormats)}
        />
      ) : null}
      <Bar
        {...buildBarSeriesProps({
          dataKey: 'value',
          name: buildBarSeriesName(activeMeasure, data.columnLabels),
          color: productThemes.pracIntel.primary,
          single: true,
          printMode,
          columns,
        })}
        cursor={onSelect ? 'pointer' : undefined}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={(barData: any) => onSelect?.(data.dimensions?.[0] ?? '', barData.label)}
      >
        <LabelList {...buildBarValueLabelProps(formatValue, columns)} />
      </Bar>
    </BarChart>
  )

  return (
    <>
      {printMode ? (
        <div className="dashboard-print-chart-frame" style={{ height: chartHeight }}>{chart}</div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          {chart}
        </ResponsiveContainer>
      )}
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.chartData.length > BAR_CHART_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : data.chartData.length}
          pageSize={backendPagination ? backendPagination.pageSize : BAR_CHART_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

export function GroupedBarViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  printMode = false,
  orientation,
  onSelect,
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  printMode?: boolean
  orientation?: DashboardWidget['orientation']
  onSelect?: (field: string, value: string) => void
}) {
  const { measureCols, dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const dimension = resolveChartDimension(data.dimensions, dimensionCols, data.rows)
  const formatCategory = (value: unknown) => formatDimensionLabel(String(value ?? ''), dimension, data.columnFormats)
  if (data.chartData.length === 0) return <p className="py-4 text-sm text-gray-400">No chart data available.</p>

  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.chartData.length / BAR_CHART_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleChartData = onPageChange && !backendPagination
    ? data.chartData.slice((currentPage - 1) * BAR_CHART_PAGE_SIZE, currentPage * BAR_CHART_PAGE_SIZE)
    : data.chartData

  const seriesNames = visibleChartData.find((p) => p.series && p.series.length > 0)?.series?.map((s) => s.name) ?? []
  const rechartsData = visibleChartData.map((point) => {
    const row: Record<string, string | number> = { label: point.label }
    for (const s of point.series ?? []) row[s.name] = s.value
    return row
  })
  const values = visibleChartData.flatMap((point) => point.series?.map((s) => s.value) ?? [point.value])
  const columns = orientation === 'vertical'
  const axisColumn = measureCols[0] ?? seriesNames[0] ?? undefined
  const layout = buildBarLayout({
    columns,
    categories: visibleChartData.map((point) => formatCategory(point.label)),
    valueAxis: buildBarValueAxisProps(values, (value) => formatCompactColumnValue(value, axisColumn, data.columnFormats), printMode),
    formatCategory,
    printMode,
  })
  const chartHeight = layout.categoryAxisHeight !== null
    ? (printMode ? 220 : 280) + layout.categoryAxisHeight
    : printMode
      ? Math.max(180, Math.min(540, rechartsData.length * 30 + 56))
      : Math.max(180, rechartsData.length * 42 + 52)
  const chart = (
    <BarChart
      data={rechartsData}
      layout={layout.layout}
      margin={layout.margin}
      barCategoryGap="24%"
      barGap={GROUPED_COLUMN_GAP}
      width={printMode ? PRINT_CHART_WIDTH : undefined}
      height={printMode ? chartHeight : undefined}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onClick={(chartData: any) => {
        const label = chartData?.activePayload?.[0]?.payload?.label
        if (label && onSelect) onSelect(data.dimensions?.[0] ?? '', String(label))
      }}
      style={onSelect ? { cursor: 'pointer' } : undefined}
    >
      {layout.grid}
      {layout.axes}
      {!printMode ? (
        <RechartsTooltip
          {...chartTooltipProps}
          labelFormatter={formatCategory}
          formatter={(value, name, item) => formatColumnValue(Number(value), String(item?.dataKey ?? name ?? measureCols[0] ?? ''), data.columnFormats)}
        />
      ) : null}
      <Legend {...chartLegendProps} />
      {seriesNames.map((name, i) => (
        <Bar
          key={name}
          {...buildBarSeriesProps({
            dataKey: name,
            name: buildBarSeriesName(name, data.columnLabels),
            color: getSeriesColor(i),
            single: false,
            printMode,
            columns,
          })}
        >
          <LabelList
            {...(columns
              ? buildGroupedColumnLabelProps((value) => formatCompactColumnValue(value, name, data.columnFormats))
              : buildBarValueLabelProps((value) => formatCompactColumnValue(value, name, data.columnFormats)))}
          />
        </Bar>
      ))}
    </BarChart>
  )

  return (
    <>
      {printMode ? (
        <div className="dashboard-print-chart-frame" style={{ height: chartHeight }}>{chart}</div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          {chart}
        </ResponsiveContainer>
      )}
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.chartData.length > BAR_CHART_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : data.chartData.length}
          pageSize={backendPagination ? backendPagination.pageSize : BAR_CHART_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

/* eslint-disable react-refresh/only-export-components -- pure LineViz prop builders, exported for Viz.line.test.tsx */
// Mock-up line chart styling (mockups/charts.js lineChart). Pure prop builders, so the tests can check them without a DOM.
export const LINE_CHART_GRID_PROPS = { stroke: CHART_GRID_COLOR, vertical: false } as const

export function lineChartXAxisProps(printMode: boolean) {
  return {
    tick: printMode ? { ...CHART_AXIS_TICK, fontSize: 9 } : CHART_AXIS_TICK,
    tickLine: false,
    axisLine: { stroke: CHART_AXIS_LINE_COLOR },
    tickMargin: 8,
    minTickGap: 18,
    interval: 'preserveStartEnd',
  } as const
}

export function buildLineGradientStops(color: string) {
  return [
    { offset: '0%', stopColor: color, stopOpacity: 0.22 },
    { offset: '100%', stopColor: color, stopOpacity: 0 },
  ] as const
}

/** Current-period series: a smooth area with a hollow dot. */
export function buildLineAreaProps(seriesKey: string, seriesKeys: string[], gradientId: string) {
  const color = getLineSeriesColor(seriesKey, seriesKeys)
  return {
    dataKey: seriesKey,
    name: seriesKey,
    type: 'monotone',
    stroke: color,
    strokeWidth: 2.5,
    fill: `url(#${gradientId})`,
    isAnimationActive: false,
    connectNulls: true,
    dot: { r: 3, fill: '#fff', stroke: color, strokeWidth: 2 },
    activeDot: { r: 5, strokeWidth: 0, fill: color },
  } as const
}

/** Prior-year series: a dashed smooth line, no fill. */
export function buildLinePriorYearProps(seriesKey: string, seriesKeys: string[]) {
  const color = getLineSeriesColor(seriesKey, seriesKeys)
  return {
    dataKey: seriesKey,
    name: seriesKey,
    type: 'monotone',
    stroke: color,
    strokeWidth: 2,
    strokeDasharray: '5 4',
    isAnimationActive: false,
    connectNulls: true,
    dot: { r: 2.5, fill: '#fff', stroke: color, strokeWidth: 2 },
  } as const
}

/* eslint-enable react-refresh/only-export-components */
export function LineViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  printMode = false,
  onSelect,
  trendGranularity = 'daily',
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  printMode?: boolean
  onSelect?: (field: string, value: string) => void
  trendGranularity?: DashboardTrendGranularity
}) {
  const { measureCols, dimensionCols } = useMemo(() => classifyColumns(data.columns, data.rows), [data.columns, data.rows])
  const gradientIdBase = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const activeMeasure = measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
  const dimension = resolveChartDimension(data.dimensions, dimensionCols, data.rows)
  // Axis and tooltip use the short `1 Jul` form, except daily points with a stored date format keep it in the tooltip.
  const formatTooltipLabel = (label: unknown) => {
    if (trendGranularity === 'daily' && resolveColumnFormat(data.columnFormats, dimension)) {
      return formatDimensionLabel(String(label ?? ''), dimension, data.columnFormats)
    }
    const shortLabel = typeof label === 'string' || typeof label === 'number' ? label : null
    return formatShortDateLabel(shortLabel, trendGranularity)
  }
  const seriesFormatColumn = (name: unknown) => {
    const column = lineSeriesColumn(String(name ?? activeMeasure))
    return resolveColumnFormat(data.columnFormats, column) ? column : activeMeasure
  }
  const sortedChartData = aggregateLineChartDataByDate(data.chartData, trendGranularity)
  const comparisonChartData = aggregateLineChartDataByDate(
    shiftChartPointLabelsByYears(data.comparisonChartData ?? [], 1),
    trendGranularity,
  )
  if (sortedChartData.length === 0) return <p className="py-4 text-sm text-gray-400">No chart data available.</p>

  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(sortedChartData.length / LINE_CHART_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleChartData = onPageChange && !backendPagination
    ? sortedChartData.slice((currentPage - 1) * LINE_CHART_PAGE_SIZE, currentPage * LINE_CHART_PAGE_SIZE)
    : sortedChartData
  const { rows: rechartsData, seriesKeys } = buildLineChartRows(
    visibleChartData,
    comparisonChartData,
    activeMeasure,
    data.comparePreviousYear === true,
  )
  const chartHeight = printMode ? 260 : 320
  // The Y axis starts at zero unless the data goes negative; otherwise it follows the data.
  const lowestValue = rechartsData.reduce((min, row) => {
    const values = seriesKeys.map((key) => row[key]).filter((value): value is number => typeof value === 'number')
    return Math.min(min, ...values)
  }, 0)
  const yAxisMin = lowestValue < 0 ? 'auto' : 0
  const currentSeriesKeys = seriesKeys.filter((seriesKey) => !seriesKey.startsWith('previous:'))
  const previousSeriesKeys = seriesKeys.filter((seriesKey) => seriesKey.startsWith('previous:'))
  const gradientId = (seriesKey: string) => `${gradientIdBase}-line-${seriesKeys.indexOf(seriesKey)}`
  const chart = (
    <ComposedChart
      data={rechartsData}
      margin={printMode
        ? { top: 4, right: 8, left: 4, bottom: 8 }
        : { top: 10, right: 12, left: 0, bottom: 0 }}
      width={printMode ? PRINT_CHART_WIDTH : undefined}
      height={printMode ? chartHeight : undefined}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onClick={(chartData: any) => {
        const label = chartData?.activePayload?.[0]?.payload?.label
        if (label && onSelect) onSelect(data.dimensions?.[0] ?? '', String(label))
      }}
      style={onSelect ? { cursor: 'pointer' } : undefined}
    >
      <defs>
        {currentSeriesKeys.map((seriesKey) => (
          <linearGradient key={seriesKey} id={gradientId(seriesKey)} x1="0" y1="0" x2="0" y2="1">
            {buildLineGradientStops(getLineSeriesColor(seriesKey, seriesKeys)).map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.stopColor} stopOpacity={stop.stopOpacity} />
            ))}
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid {...LINE_CHART_GRID_PROPS} />
      <XAxis
        dataKey="label"
        {...lineChartXAxisProps(printMode)}
        tickFormatter={(value) => formatShortDateLabel(value, trendGranularity)}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={printMode ? { ...CHART_AXIS_TICK, fontSize: 9 } : CHART_AXIS_TICK}
        width={printMode ? 44 : 56}
        tickCount={5}
        domain={[yAxisMin, 'auto']}
        tickFormatter={(value) => formatCompactColumnValue(Number(value), activeMeasure, data.columnFormats)}
      />
      {!printMode ? (
        <RechartsTooltip
          {...chartTooltipProps}
          labelFormatter={formatTooltipLabel}
          formatter={(value, name) => [formatColumnValue(Number(value), seriesFormatColumn(name), data.columnFormats), formatLineSeriesName(String(name ?? activeMeasure))]}
        />
      ) : null}
      {seriesKeys.length > 1 ? (
        <Legend
          {...chartLegendProps}
          wrapperStyle={printMode ? { fontSize: 9 } : undefined}
          formatter={(value) => <span style={{ color: '#475467', fontSize: printMode ? 9 : 12 }}>{formatLineSeriesName(String(value))}</span>}
        />
      ) : null}
      {currentSeriesKeys.map((seriesKey) => (
        <Area key={seriesKey} {...buildLineAreaProps(seriesKey, seriesKeys, gradientId(seriesKey))} />
      ))}
      {previousSeriesKeys.map((seriesKey) => (
        <Line key={seriesKey} {...buildLinePriorYearProps(seriesKey, seriesKeys)} />
      ))}
    </ComposedChart>
  )

  return (
    <>
      {printMode ? (
        <div className="dashboard-print-chart-frame" style={{ height: chartHeight }}>{chart}</div>
      ) : (
        <div style={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            {chart}
          </ResponsiveContainer>
        </div>
      )}
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : sortedChartData.length > LINE_CHART_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : sortedChartData.length}
          pageSize={backendPagination ? backendPagination.pageSize : LINE_CHART_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

export function TableViz({
  data,
  page = 1,
  onPageChange,
  showPagination = true,
  onSelect,
  onSortChange,
  sortColumn,
  sortDirection,
  expandedRows,
  loadingRowKeys,
  onExpandRow,
  canExpandRow,
  buildRowKey,
}: {
  data: AgentBuilderReportData
  page?: number
  onPageChange?: (p: number) => void
  showPagination?: boolean
  onSelect?: (field: string, value: string) => void
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void
  sortColumn?: string | null
  sortDirection?: 'asc' | 'desc' | null
  expandedRows?: Record<string, ExpandedTableResult>
  loadingRowKeys?: Record<string, boolean>
  onExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => void
  canExpandRow?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => boolean
  buildRowKey?: (row: Record<string, unknown>, columns: string[], parentKey?: string) => string
}) {
  if (onExpandRow) {
    return (
      <ExpandableTable
        data={data}
        page={page}
        onPageChange={onPageChange}
        showPagination={showPagination}
        onSortChange={onSortChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        expandedRows={expandedRows}
        loadingRowKeys={loadingRowKeys}
        onExpandRow={onExpandRow}
        canExpandRow={canExpandRow}
        buildRowKey={buildRowKey}
      />
    )
  }
  const backendPagination = resolveBackendPagination(data)
  const localPageCount = Math.max(1, Math.ceil(data.rows.length / TABLE_PAGE_SIZE))
  const currentPage = backendPagination ? backendPagination.page : Math.min(Math.max(page, 1), localPageCount)
  const visibleRows = onPageChange && !backendPagination
    ? data.rows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE)
    : data.rows
  const numericColumns = resolveNumericColumns(data)
  return (
    <>
      <div
        className={`dashboard-print-table-scroll overflow-auto ${TABLE_WRAPPER_CLASS} ${showPagination ? 'max-h-[32rem]' : ''}`}
        style={getPrintTableStyle(data.columns.length)}
      >
        <table className="w-full text-sm">
          <thead>
            <tr>
              {data.columns.map((column) => (
                <SortableHeader
                  key={column}
                  column={column}
                  label={data.columnLabels?.[column]}
                  active={sortColumn === column}
                  direction={sortDirection}
                  onSortChange={onSortChange}
                  align={numericColumns.has(column) ? 'right' : 'left'}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(data.columns.length, 1)} className="px-3 py-6 text-center text-sm text-gray-400">
                  No rows to display.
                </td>
              </tr>
            ) : (
              visibleRows.map((row, i) => (
                <tr
                  key={i}
                  className={`${TABLE_BODY_ROW_CLASS} ${onSelect ? 'feature-hover-surface cursor-pointer' : 'hover:bg-[#f8fbfb]'}`}
                  onClick={() => {
                    if (!onSelect) return
                    const dimCol = data.columns.find((col) => typeof row[col] === 'string')
                    if (dimCol) onSelect(dimCol, String(row[dimCol]))
                  }}
                >
                  {data.columns.map((column) => {
                    const value = row[column]
                    const numeric = numericColumns.has(column)
                    return (
                      <td key={column} className={tableBodyCellClass(numeric, value)}>
                        {renderPlainCell(value, column, data.columnFormats, numeric)}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showPagination && onPageChange && (backendPagination ? backendPagination.totalRows > backendPagination.pageSize : data.rows.length > TABLE_PAGE_SIZE) && (
        <PaginationBar
          page={currentPage}
          totalRows={backendPagination ? backendPagination.totalRows : data.rows.length}
          pageSize={backendPagination ? backendPagination.pageSize : TABLE_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}
